import MuiButton from "@mui/material/Button";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import type { ButtonEmphasis, ButtonProps } from "../types";

const EMPHASIS_TO_MUI: Record<ButtonEmphasis, { variant: "contained" | "outlined" | "text" }> = {
  primary: { variant: "contained" },
  secondary: { variant: "outlined" },
  quiet: { variant: "text" },
};

/**
 * Containment note (types.ts): the root component is fixed to `'button'`.
 * Material UI's polymorphic `component` prop, and therefore `href`/
 * `target`, are never forwarded — this type has no way to render an
 * anchor.
 */
const target = tokens.interactionState.targetSize;

export function Button({
  emphasis,
  children,
  type = "button",
  disabled,
  visuallyDisabled,
  fullWidth,
  size,
  startIcon,
  endIcon,
  onClick,
  id,
  name,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  testId,
  narrowFullWidth,
}: Readonly<ButtonProps>) {
  // `disabled` wins if both are set: a genuinely disabled control has no
  // need for a look-disabled-but-operable treatment on top of it.
  const showVisuallyDisabled = visuallyDisabled === true && disabled !== true;
  return (
    <MuiButton
      variant={EMPHASIS_TO_MUI[emphasis].variant}
      color="primary"
      type={type}
      // Non-styling marker (FIND-30 / OBJ-15): `emphasis` otherwise only
      // ever selects MUI's `variant`, a purely visual prop with no DOM
      // trace of its own. A gate that must count "the primary marker
      // rather than by inspecting styling" needs something to count that
      // isn't styling — this attribute is that marker.
      data-emphasis={emphasis}
      {...cleanProps({
        disabled,
        fullWidth,
        startIcon,
        endIcon,
        onClick,
        id,
        name,
        "aria-describedby": ariaDescribedBy,
        "aria-expanded": ariaExpanded,
        "data-testid": testId,
        // Deliberately never the native `disabled` prop here (see
        // ButtonProps.visuallyDisabled's own doc comment) -- `aria-disabled`
        // plus this dimmed styling looks the same as MUI's own disabled
        // treatment without removing the control from the tab order or
        // blocking the click/Enter that must still reach the handler.
        "aria-disabled": showVisuallyDisabled ? true : undefined,
        // ADR-20261102-h2fd: the H2 design system's Button sizes itself -- `large` is the touch
        // target plus 8px with 32px side padding, anything else the touch target with 24px --
        // rather than handing `size` to Material UI, whose own sizes change the type scale.
        sx: {
          minHeight: size === "large" ? target.touch + tokens.spacing.xs : target.touch,
          minWidth: target.standard,
          px: `${size === "large" ? tokens.spacing.xl : tokens.spacing.lg}px`,
          // H2 sets full width below 600px button by button (`full-width="{{ narrow }}"`); in an
          // action row the row's own stretch does it.
          ...(fullWidth === true ? {} : narrowFullWidth === true ? { width: { xs: "100%", sm: "auto" } } : {}),
          ...(showVisuallyDisabled ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "auto" } : {}),
        },
      })}
    >
      {children}
    </MuiButton>
  );
}
